import React from 'react';
import classNameBind from 'classnames/bind';
import { TFunction } from 'react-i18next';
import {
  Text,
  Link,
  Stack,
  Trans,
  OrderedList,
  Icon,
} from '@piwikpro/ui-components';
import { CopyIcon } from '@piwikpro/platform';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import styles from '../InstallationMethods.module.css';
import { App, ConsentManagementTool } from '../../../model';

const bindClasses = classNameBind.bind(styles);

const inlineWordpressError = (t: TFunction) => (
  <Stack spacing="narrow" verticalAlignment="center">
    <Stack.Item />
    <Stack.Item>
      <Icon name="exclamation-mark-circle" color="red" />
    </Stack.Item>
    <Stack.Item>
      <Text color="Red" dataId="TextStyle">
        <div style={{ marginBottom: '2px' }}>
          {t('inline-error.data-unavailable')}
        </div>
      </Text>
    </Stack.Item>
  </Stack>
);

interface WordPressInstallProps {
  app: App
  containerUrl: string
  containerUrlFailed: boolean
  productAnalytics: ProductAnalytics
  t: TFunction
  cmp: ConsentManagementTool | null
}

const WordPressInstall: React.FC<WordPressInstallProps> = ({
  app,
  containerUrl,
  containerUrlFailed,
  productAnalytics,
  t,
  cmp,
}) => (
  <>
    <OrderedList>
      <Stack vertical>
        <Stack.Item>
          <Stack vertical spacing="narrow">
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line1.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line1.part2-plugins')}
                </Text>
                {' '}
                &gt;
                {' '}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line1.part2-new-plugin')}
                </Text>
                .
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line2.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line2.part2')}
                </Text>
                {t('apps.details.installation.wordpress.instruction.line2.part3')}
                <Link href="https://wordpress.org/plugins/piwik-pro/">
                  {t('apps.details.installation.links.wordpressPluginInstall')}
                </Link>
                .
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line3.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line3.part2')}
                </Text>
                .
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line4.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line4.part2')}
                </Text>
                .
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line5.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line5.part2-settings')}
                </Text>
                {' '}
                &gt;
                {' '}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line5.part2-piwik-pro')}
                </Text>
                .
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <div className={bindClasses('copy-content-wrapper')}>
                <OrderedList.Item>
                  <Trans
                    i18nKey="apps.details.installation.wordpress.instruction.line6-with-address"
                    values={{
                      address: containerUrl,
                    }}
                    components={[
                      <Text color="InkLighter" dataId="TextStyle">appId</Text>,
                    ]}
                  />
                </OrderedList.Item>
                {containerUrlFailed
                  ? inlineWordpressError(t)
                  : <CopyIcon value={containerUrl} />}
              </div>
            </Stack.Item>
            <Stack.Item>
              <div className={bindClasses('copy-content-wrapper')}>
                <OrderedList.Item>
                  <Trans
                    i18nKey="apps.details.installation.wordpress.instruction.line7-new"
                    values={{
                      appId: app.id,
                    }}
                    components={[
                      <Text color="InkLighter" dataId="TextStyle">appId</Text>,
                    ]}
                  />
                </OrderedList.Item>
                <CopyIcon value={app.id} />
              </div>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line10')}
              </OrderedList.Item>
            </Stack.Item>
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line11.part1')}
                <Text weight={600} dataId="TextStyle">
                  {t('apps.details.installation.wordpress.instruction.line11.part2')}
                </Text>
                .
              </OrderedList.Item>
            </Stack.Item>
            {cmp === 'CookieInformationCMP' && (
              <Stack.Item>
                <OrderedList.Item>
                  <Trans
                    t={t}
                    i18nKey="apps.details.installation.wordpress.instruction.line2-ci"
                    components={[
                      <Link href="https://support.cookieinformation.com/en/articles/9717257-set-up-google-consent-mode-v2-using-wordpress-plugin#h_757dd4cce4">
                        this step-by-step guide
                      </Link>,
                    ]}
                  />
                </OrderedList.Item>
              </Stack.Item>
            )}
            {cmp === 'OtherConsentTool' && (
              <Stack.Item>
                <OrderedList.Item>
                  <Trans
                    t={t}
                    i18nKey="apps.details.installation.wordpress.instruction.line2-other"
                    components={[
                      <Link href="https://help.piwik.pro/support/privacy/how-can-i-integrate-a-third-party-cookie-consent-tool-with-piwik-pro/">
                        installation guide
                      </Link>,
                    ]}
                  />
                </OrderedList.Item>
              </Stack.Item>
            )}
            {cmp === 'PiwikLegacy' && (
              <Stack.Item>
                <OrderedList.Item>
                  {t('apps.details.installation.wordpress.instruction.line13.part1')}
                  <Link href="https://help.piwik.pro/support/consent-manager/update-link-privacy-policy-page/">
                    {t('apps.details.installation.links.privacyPolicyAddress')}
                  </Link>
                  {t('apps.details.installation.wordpress.instruction.line13.part2')}
                  <Link href="https://help.piwik.pro/support/consent-manager/setting-consent-manager/">
                    {t('apps.details.installation.links.settingsConsent')}
                  </Link>
                  .
                </OrderedList.Item>
              </Stack.Item>
            )}
            <Stack.Item>
              <OrderedList.Item>
                {t('apps.details.installation.wordpress.instruction.line12.part1')}
                <Link
                  onClick={() => {
                    productAnalytics.push({ type: '@platform/command/appInstallation/wordpress/tracker-debugger' });
                  }}
                  href={`/analytics/#/${app.id}/settings/debugger/`}
                >
                  {t('apps.details.installation.links.trackerDebugger')}
                </Link>
                {t('apps.details.installation.wordpress.instruction.line12.part2')}
              </OrderedList.Item>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </OrderedList>
    <Stack vertical>
      <Stack.Item previousSpacing="wide" />
      <Stack.Item>
        <Text as="p">
          {t('apps.details.installation.instruction.help.part1')}
          <Link href="https://help.piwik.pro/support/getting-started/wordpress-install-a-tracking-code/">
            {t('apps.details.installation.links.installGuide')}
          </Link>
          {t('apps.details.installation.instruction.help.part2')}
          <Link href="http://community.piwik.pro/">
            {t('apps.details.installation.links.community')}
          </Link>
          .
        </Text>
      </Stack.Item>
    </Stack>
  </>
);

export default WordPressInstall;
